import {
  FilterSearchSpecificFilterFnKey,
  FilterSearchCustomFn,
} from '@/common/components/molecules/filterSearch/filterSearch.type';
import {
  compareNumberValues,
  compareTimeValues,
} from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { isNil } from 'lodash-es';
import {
  BPS_BY_UNIT_LOWER_CASE,
  BYTES_BY_UNIT_LOWER_CASE,
  MS_BY_UNIT,
} from '@/common/utils/define';
import { convertMsToSec } from '@/common/utils/core/number/conversion';
import { formatBytes } from '@/common/utils/commonUtils';
import { CoreUnit } from '@/common/utils/convertUnits.types';
import { AGE_UNITS, AgeUnit } from '@/common/components/molecules/filterSearch/filterSearch.define';

type Visible = boolean;

const parseTimeValueUnits = (targetValue: string): { value: number; unit: string }[] => {
  const regex = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*(${Object.keys(MS_BY_UNIT).join('|')})?`, 'gi');

  const matches = [...targetValue.matchAll(regex)];

  return matches.map((match) => ({
    value: parseFloat(match[1]),
    unit: match[2],
  }));
};

const parseDataValueUnit = (
  targetValue: string,
): {
  value: string | undefined;
  unit: string | undefined;
} => {
  const regex = /^(\d+(?:\.\d+)?)\s*([kmgtpezy]i?b|bytes?|b)?$/i;
  const match = targetValue.match(regex);
  return { value: match?.[1], unit: match?.[2] };
};

const parseBpsValueUnit = (
  targetValue: string,
): {
  value: string | undefined;
  unit: string | undefined;
} => {
  const regex = /^(\d+(?:\.\d+)?)\s*([kmgt]?i?b(?:it|yte)?\/s|[kmgt]?bps|bit|byte)?$/i;
  const match = targetValue.match(regex);
  return { value: match?.[1], unit: match?.[2] };
};

export const stringToMs = (targetValue: string, defaultUnit?: string): number | undefined => {
  const parsedUnits = parseTimeValueUnits(targetValue);

  if (parsedUnits.length === 0) return undefined;

  return parsedUnits.reduce((totalMs, { value, unit }) => {
    const msByUnit = MS_BY_UNIT[unit] ?? MS_BY_UNIT[defaultUnit ?? ''];
    return totalMs + (msByUnit ? value * msByUnit : 0);
  }, 0);
};

export const stringToBytes = (targetValue: string, defaultUnit?: string): number | undefined => {
  const { value, unit } = parseDataValueUnit(targetValue);
  const bytesByUnit = BYTES_BY_UNIT_LOWER_CASE[(unit || defaultUnit)?.toLowerCase() ?? ''];

  if (value === undefined) {
    return undefined;
  }

  if (bytesByUnit) {
    return parseFloat(value) * bytesByUnit;
  }

  return parseFloat(value);
};

export const stringToBps = (targetValue: string, defaultUnit?: string): number | undefined => {
  const { value, unit } = parseBpsValueUnit(targetValue);
  const bpsByUnit = BPS_BY_UNIT_LOWER_CASE[(unit || defaultUnit)?.toLowerCase() ?? ''];

  if (value === undefined) {
    return undefined;
  }

  if (bpsByUnit) {
    return parseFloat(value) * bpsByUnit;
  }

  return parseFloat(value);
};

export const stringToW = (targetValue: string): number | undefined => {
  const regex = /^(\d+(?:\.\d+)?)\s*([mM]?[wW])?$/;
  const match = targetValue.match(regex);

  if (!match) return undefined;

  const value = parseFloat(match[1]);
  const unit = match[2]?.toLowerCase();

  if (unit && unit.startsWith('m')) {
    return value / 1000;
  }

  return value;
};

const convertBytesToMiB = (bytes: number): number => {
  return bytes / (1024 * 1024);
};

export const stringToMiB = (targetValue: string): number | undefined => {
  const regex = /^(\d+(?:\.\d+)?)\s*([kmgtpezy]i?b|bytes?|b)?$/i;
  const match = targetValue.match(regex);

  if (!match) return undefined;

  const value = parseFloat(match[1]);
  const unit = match[2]?.toLowerCase();

  if (unit) {
    const byteValue = stringToBytes(targetValue);
    return byteValue !== undefined ? convertBytesToMiB(byteValue) : undefined;
  }

  return value;
};

const CORE_UNIT_REGEX = new RegExp(`^(\\d+(?:\\.\\d+)?)\\s*(${CoreUnit.join('|')})?$`, 'i');
const MILLICORE_INDEX = CoreUnit.indexOf('millicore');

/**
 * CPU 검색어를 raw millicore 값으로 변환한다. CoreUnit 전 단계(nanocore~exacore) 접미사를
 * 지원하며, 접미사가 없으면 millicore로 간주한다. 해석할 수 없으면 undefined를 반환한다.
 * @example stringToMillicore('1.5 core') // 1500
 */
export const stringToMillicore = (targetValue: string): number | undefined => {
  const match = CORE_UNIT_REGEX.exec(targetValue.trim());
  if (!match) {
    return undefined;
  }

  const [, numberText, unitText] = match;
  const value = Number(numberText);
  if (!unitText) {
    return value;
  }

  const unitIndex = CoreUnit.findIndex((unit) => unit === unitText.toLowerCase());
  const scaleToMillicore = 1000 ** (unitIndex - MILLICORE_INDEX);
  return value * scaleToMillicore;
};

// 경과시간(age) 단위 토큰 → 캐노니컬 AGE_UNITS 키. 별칭(영문 약어·풀네임·한글) 포함.
// 대문자 'M'(월)은 키에 없고 `normalizeAgeToSeconds`에서 소문자 'm'(분)과 구분해 별도 처리한다.
const AGE_UNIT_ALIAS_MAP: Record<string, AgeUnit> = {
  s: 's',
  sec: 's',
  secs: 's',
  second: 's',
  seconds: 's',
  초: 's',
  m: 'm',
  min: 'm',
  mins: 'm',
  minute: 'm',
  minutes: 'm',
  분: 'm',
  h: 'h',
  hr: 'h',
  hrs: 'h',
  hour: 'h',
  hours: 'h',
  시: 'h',
  시간: 'h',
  d: 'd',
  day: 'd',
  days: 'd',
  일: 'd',
  mo: 'M',
  mon: 'M',
  month: 'M',
  months: 'M',
  월: 'M',
  개월: 'M',
  y: 'y',
  yr: 'y',
  yrs: 'y',
  year: 'y',
  years: 'y',
  연: 'y',
  년: 'y',
};

// 문자열 전체가 '숫자(+소수)+age단위' 토큰의 반복으로만 구성될 때만 경과시간 문자열로 본다.
// 단위 토큰을 AGE_UNIT_ALIAS_MAP 키(별칭 포함)로 게이트해 '10 pods'·'12.5GB'·ISO 날짜 등
// 비-age 문자열을 배제한다. 별칭 prefix 오매칭(min↔minute, s↔sec)을 피하려고 긴 토큰부터 매칭한다.
const AGE_UNIT_TOKEN_PATTERN = Object.keys(AGE_UNIT_ALIAS_MAP)
  .sort((a, b) => b.length - a.length)
  .map((unit) => unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  .join('|');
const AGE_STRING_PATTERN = new RegExp(
  `^(?:\\s*\\d+(?:\\.\\d+)?\\s*(?:${AGE_UNIT_TOKEN_PATTERN}))+\\s*$`,
  'i',
);

/**
 * 값이 경과시간(age) 문자열인지 판별한다 — `숫자(+소수)+age단위` 토큰의 반복으로만 구성될 때만 true.
 *
 * 단위를 `normalizeAgeToSeconds`와 동일한 별칭 집합으로 게이트하므로, '10 pods'·'12.5GB' 같은
 * `<숫자> <비-age 단어>`나 ISO 날짜는 false다. 값 기반 age 자동 감지(예: 헥사곤 그룹 대표값)에서
 * `normalizeAgeToSeconds`의 단위 폴백('s')으로 비-age 문자열이 초로 오환산되는 것을 막는다.
 */
export const isAgeString = (value: string): boolean => AGE_STRING_PATTERN.test(value);

export const normalizeAgeToSeconds = (age: string | number | null | undefined): number => {
  const pattern = /(\d+(?:\.\d+)?)\s*([a-zA-Z가-힣]*)/g;
  let totalSeconds = 0;

  if (isNil(age)) return 0;

  const ageStr = typeof age === 'number' ? String(age) : age;
  if (!ageStr.trim()) return 0;

  const matches = Array.from(ageStr.matchAll(pattern));

  matches.forEach((match) => {
    const [, value, unit = ''] = match;
    const normalizedUnit =
      unit === 'M' ? 'M' : (AGE_UNIT_ALIAS_MAP[unit.trim().toLowerCase()] ?? 's');
    totalSeconds += parseFloat(value) * AGE_UNITS[normalizedUnit as AgeUnit];
  });

  return totalSeconds;
};

export const FILTER_CONDITION_BY_SEARCH_TYPE: Record<
  FilterSearchSpecificFilterFnKey,
  FilterSearchCustomFn
> = {
  toMs: (cellValue, token): Visible => {
    // cellValue: 단위 없으면 ms 취급
    // searchValue: 단위 없으면 ms 취급
    return compareNumberValues({
      cellValue: stringToMs(`${cellValue}`, 'ms'),
      searchValue: stringToMs(`${token.value}`, 'ms'),
      operator: token.operator,
    });
  },

  toBytes: (cellValue, token): Visible => {
    // cellValue: 단위 없으면 byte 취급
    // searchValue: 단위 없으면 byte 취급
    return compareNumberValues({
      cellValue: stringToBytes(`${cellValue}`, 'byte'),
      searchValue: stringToBytes(`${token.value}`, 'byte'),
      operator: token.operator,
    });
  },

  toKiB: (cellValue, token): Visible => {
    // cellValue: 단위 없으면 KiB 취급
    // searchValue: 단위 없으면 KiB 취급
    return compareNumberValues({
      cellValue: stringToBytes(`${cellValue}`, 'KiB'),
      searchValue: stringToBytes(`${token.value}`, 'KiB'),
      operator: token.operator,
    });
  },

  toMB: (cellValue, token): Visible => {
    // cellValue: 단위 없으면 MB 취급
    // searchValue: 단위 없으면 MB 취급
    return compareNumberValues({
      cellValue: stringToBytes(`${cellValue}`, 'MB'),
      searchValue: stringToBytes(`${token.value}`, 'MB'),
      operator: token.operator,
    });
  },

  // bytes 단위의 값인데 rendererType 없이 grid template으로 읽기 편한 단위로 올려서 표시중인 컬럼 권장
  readableBytes: (cellValue, token): Visible => {
    // 매칭 연산자: formatBytes 결과를 문자열로 비교 (보이는 그대로의 비교)
    // formatBytes는 크기에 따라 단위가 달라지므로 (KiB, MiB 등) 숫자 비교가 아닌 문자열 비교 필요
    if (token.operator === '==' || token.operator === '!=') {
      const searchBytes = stringToBytes(token.value, 'byte');
      const cellBytes = stringToBytes(`${cellValue}`, 'byte');
      if (searchBytes === undefined || cellBytes === undefined) return false;

      const isMatch = formatBytes(cellBytes, 'Bytes', 3) === formatBytes(searchBytes, 'Bytes', 3);
      return token.operator === '==' ? isMatch : !isMatch;
    }

    // 범위 연산자에 대한 처리 raw bytes 비교 (정밀도 유지)
    return compareNumberValues({
      cellValue: stringToBytes(`${cellValue}`, 'byte'),
      searchValue: stringToBytes(`${token.value}`, 'byte'),
      operator: token.operator,
    });
  },

  // rendererType: sec 에 사용 권장
  rendererTypeSec: (cellValue, token): Visible => {
    // 매칭 연산자에 대한 처리 sec 단위로 변환 후 비교 (보이는 그대로의 비교)
    if (token.operator === '==' || token.operator === '!=') {
      return compareNumberValues({
        cellValue: convertMsToSec(cellValue ?? ''),
        searchValue: convertMsToSec(stringToMs(token.value, 's') ?? ''),
        operator: token.operator,
      });
    }

    // 범위 연산자에 대한 처리 raw ms 비교 (정밀도 유지)
    return compareNumberValues({
      cellValue,
      searchValue: stringToMs(token.value, 's'),
      operator: token.operator,
    });
  },

  // rendererType: toSec 에 사용 권장
  // cellValue: getSpecificFilterCellValue에 의해 `"${rawValue} ${attrs.unit || ms}"` 형태로 전달됨
  toSecByUnit: (cellValue, token): Visible => {
    // 매칭 연산자에 대한 처리 sec 단위로 변환 후 비교 (보이는 그대로의 비교)
    if (token.operator === '==' || token.operator === '!=') {
      return compareNumberValues({
        cellValue: convertMsToSec(stringToMs(`${cellValue}`, 's') ?? ''),
        searchValue: convertMsToSec(stringToMs(token.value, 's') ?? ''),
        operator: token.operator,
      });
    }

    // 범위 연산자에 대한 처리 raw ms 비교 (정밀도 유지)
    return compareNumberValues({
      cellValue: stringToMs(`${cellValue}`, 's'),
      searchValue: stringToMs(token.value, 's'),
      operator: token.operator,
    });
  },

  toBps: (cellValue, token): Visible => {
    // cellValue: 단위 없으면 bps 취급
    // searchValue: 단위 없으면 bps 취급
    return compareNumberValues({
      cellValue: stringToBps(`${cellValue}`, 'bps'),
      searchValue: stringToBps(`${token.value}`, 'bps'),
      operator: token.operator,
    });
  },

  bytesToMiB: (cellValue, token): Visible => {
    const cellValueMiB = convertBytesToMiB(Number(cellValue));
    const searchValueMiB = stringToMiB(token.value);

    if (searchValueMiB === undefined) return false;

    return compareNumberValues({
      cellValue: cellValueMiB,
      searchValue: searchValueMiB,
      operator: token.operator,
    });
  },

  mWToW: (cellValue, token): Visible => {
    return compareNumberValues({
      cellValue: cellValue / 1000,
      searchValue: stringToW(token.value),
      operator: token.operator,
    });
  },

  toUnixTime: (cellValue, token): Visible => {
    return compareTimeValues({
      cellValue,
      searchValue: token.value,
      operator: token.operator,
    });
  },

  // age (upTime) 단위 비교
  age: (cellValue, token) => {
    return compareNumberValues({
      cellValue: normalizeAgeToSeconds(cellValue),
      searchValue: normalizeAgeToSeconds(token.value),
      operator: token.operator,
    });
  },
};
